import React, { useEffect } from "react";
import { Button, Modal, useWizard } from "@picklebet/ui";
import { ReactComponent as CancelledArt } from "features/wallet/assets/cancelled.svg";
import { ReactComponent as FailedArt } from "features/wallet/assets/failed.svg";
import { ReactComponent as PendingArt } from "features/wallet/assets/pending.svg";
import { LinkButton } from "@/features/shell/components/shared/LinkButton/LinkButton";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { useSkrillDeposit } from "@/hooks/firestore/user/useSkrillDeposits";
import { walletService } from "@/features/wallet/services/wallet";
import { useDepositWizard } from "@/features/wallet/components/deposit/hooks/useDepositWizard";
import { useLastUsedDepositMethod } from "@/features/wallet/components/deposit/hooks/useLastUsedDepositMethod";
import { DepositSuccess } from "../../blocks/deposit-success/DepositSuccess";
import type { DepositMethodStepName } from "features/wallet/components/deposit/utils/registry";

import * as styles from "./SkrillStatusStep.module.scss";

const StatusScreen = ({
  art,
  title,
  subtitle,
  children,
}: {
  art: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) => {
  return (
    <div className={styles.container}>
      {art}

      <div className={styles.title}>
        <h3>{title}</h3>
        <h5>{subtitle}</h5>
      </div>

      {children}
    </div>
  );
};

export const SkrillStatusStep = () => {
  const { depositReturn } = useDepositWizard();
  const { goTo } = useWizard<DepositMethodStepName>();
  const currency = useFiatCurrency();
  const [_, setLastUsedMethodId] = useLastUsedDepositMethod();

  const { deposit: transaction } = useSkrillDeposit(
    depositReturn.cancelled ? undefined : depositReturn.transactionId,
  );

  const { transactionId, status, firstTime } = transaction ?? {};

  useEffect(() => {
    if (status === "PROCESSED" && firstTime === true) {
      walletService.firstTimeDeposit({ currency, method: "skrill" });
    }
  }, [transactionId, status, firstTime, currency]);

  useEffect(() => {
    if (status === "PROCESSED") {
      setLastUsedMethodId("skrill");
    }
  }, [status, setLastUsedMethodId]);

  if (depositReturn.cancelled) {
    return (
      <StatusScreen
        art={<CancelledArt className={styles.art} />}
        title="Deposit Cancelled"
        subtitle="There was a problem with your deposit attempt."
      >
        <Button onClick={() => goTo("deposit", { replace: true })}>
          Try Again
        </Button>
      </StatusScreen>
    );
  }

  if (transaction?.status === "PROCESSED") {
    return <DepositSuccess />;
  }

  if (transaction?.status === "FAILED") {
    return (
      <StatusScreen
        art={<FailedArt className={styles.art} />}
        title="Deposit Failed"
        subtitle="There was a problem with your deposit attempt."
      >
        <Button onClick={() => goTo("deposit", { replace: true })}>
          Try Again
        </Button>
      </StatusScreen>
    );
  }

  return (
    <StatusScreen
      art={<PendingArt className={styles.art} />}
      title="Deposit Pending"
      subtitle="Your payment is currently pending. You will receive a notification once it is processed."
    >
      <Modal.Close>
        <LinkButton to="/wallet/overview">View Statement</LinkButton>
      </Modal.Close>
    </StatusScreen>
  );
};
